import { AccountVerification, Data, Modal, UI, Utils } from 'common';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import reduce from 'lodash/reduce';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { RoutePath } from '../utils/RoutePath';
import ContactInfoForms, { validate } from './ContactInfoForms.component';
import ContactInfoViewMode from './ContactInfoViewMode.enum';

const { AccountVerificationModal } = AccountVerification;
const { Session, Util, Logger, UserRole, withLayoutAndModalContexts } = Utils;
const { ModalButtonActionType } = Modal;
const {
	WrappedButton,
	WrappedCheckbox,
	SaveConfirmation,
	Spinner,
	WrappedTooltip,
} = UI;
const { AccountStatus } = Data;

class ContactInfo extends Component {
	constructor(props) {
		super(props);

		this.setContactFormStateDirty(false);

		this.state = {
			mainContactInfo: {},
			billingContactInfo: {},

			// placeholder for storing the inital data
			initialMainContactInfo: {},
			initialBillingContactInfo: {},

			// default validation
			validationErrors: {
				mainContactInfoErrors: this.buildDefaultValidationObject(),
				billingContactInfoErrors: this.buildDefaultValidationObject(),
			},
			clean: true,
			isLoading: true,
			loadingMessage: 'Loading contact info...',
			successfulContactUpdate: false,
			contactInfoViewMode: this.getContactView(),

			// "Same as Main Contact" checkbox is always unchecked on page load.
			// When checked, billing contacts fields should be disabled and getting
			// live updated when user updates Main Contact
			sameAsMainContact: false,

			// the value of 'IsBusinessAddress' in MainContact for enterprise users or
			// AccountContactInfo for non-enterprise users
			isBusinessAddress: false,
		};
	}

	componentDidMount() {
		const { contactInfoViewMode } = this.state;

		if (contactInfoViewMode === ContactInfoViewMode.CorporationAdmin) {
			this.getCorporationContactInfo();
		} else {
			this.getCustomerContactInfo();
		}
	}

	/**
	 * Format phone number and zip code in the data.
	 * @param {object} data Data received from 'getCustomerContactInfo' or 'getCorporationContactInfo'.
	 * @return {object}
	 */
	buildContactInfo = data => {
		// format Zip
		const Address = isObject(data.Address)
			? {
					...data.Address,
					Zip: Util.formatZip(data.Address.Zip),
				}
			: {};

		return {
			...data,
			Address,
			Email: isString(data.Email) ? data.Email : '',

			// format phone number
			PhoneNumber: Util.formatPhoneNumber(data.PhoneNumber),
		};
	};

	/**
	 * Return params object for updateCustomerContactInfo or updateCorporationContactInfo endpoint.
	 * @param {string} key 'mainContactInfo' or 'billingContactInfo'.
	 * @return {object}
	 */
	buildUpdateContactInfoParamForKey = key => {
		const {
			mainContactInfo,
			billingContactInfo,
			initialMainContactInfo,
			isBusinessAddress,
			contactInfoViewMode,
		} = this.state;

		if (contactInfoViewMode === ContactInfoViewMode.CorporationAdmin) {
			// include Business Address checkbox value
			const mainContactInfoAddress = isObject(mainContactInfo.Address)
				? {
						...mainContactInfo.Address,
						IsBusinessAddress: isBusinessAddress,
					}
				: {};

			// include Business Address checkbox value
			const billingContactInfoAddress = isObject(billingContactInfo.Address)
				? {
						...billingContactInfo.Address,
						IsBusinessAddress: isBusinessAddress,
					}
				: {};

			// build params object
			const params = {
				mainContactInfo: {
					...mainContactInfo,
					Address: mainContactInfoAddress,
				},

				billingContactInfo: {
					...billingContactInfo,
					Address: billingContactInfoAddress,
				},
			};

			return isObject(params[key]) ? params[key] : {};
		} else if (key === 'mainContactInfo') {
			return Util.differences(mainContactInfo, initialMainContactInfo);
		}
	};

	/**
	 * Return default validation object.
	 * @return {object}
	 */
	buildDefaultValidationObject = () => ({
		FirstName: false,
		LastName: false,
		Address1: false,
		City: false,
		Zip: false,
		PhoneNumber: false,
		Email: false,
	});

	/**
	 * Return an array of changed fields for Splunk logs.
	 * @param {boolean} isCorporate `true` for including billing contact update.
	 * @return {array}
	 */
	buildChangedFields = isCorporate => {
		const {
			mainContactInfo,
			billingContactInfo,
			initialMainContactInfo,
			initialBillingContactInfo,
			sameAsMainContact,
		} = this.state;

		// build main contact diff array
		const mainContactDiff = Util.differences(
			mainContactInfo,
			initialMainContactInfo
		);
		const mainContactDiffArray = Util.convertObjectToArray(mainContactDiff);

		// build billing contact diff array
		// use main contact info for comparison if "Same as Main Contact" is checked
		const billingContactInfoDiff = Util.differences(
			sameAsMainContact ? mainContactInfo : billingContactInfo,
			initialBillingContactInfo
		);
		const billingContactDiffArray = Util.convertObjectToArray(
			billingContactInfoDiff,
			'BillingContact'
		);

		return isCorporate
			? mainContactDiffArray.concat(billingContactDiffArray)
			: mainContactDiffArray;
	};

	setContactFormStateDirty = isDirty => {
		const { enterpriseLocation } = this.props;

		if (!enterpriseLocation) {
			Session.setFormStateDirty(isDirty);
		}
	};

	/**
	 * Return a view mode per user's role.
	 * See https://stamps.atlassian.net/wiki/spaces/PRODREQ/pages/3122154/MyAccount+-+Contact+Info
	 */
	getContactView = () => {
		const { enterpriseLocation } = this.props;
		const userRole = Session.getUserRole();
		const { location } = this.props;

		// check on URL path, '/contactinfo' or '/locationcontactinfo'
		const isPathLocationContactInfo =
			location && location.pathname === RoutePath.locationContactInfo;

		/**
		 * If user is a corp admin (or a region admin), there are two account data user
		 * can see, corporation account and meter account.
		 *
		 * Corporation account can be viewed through "ContactInfo" tab under "Corporate Settings".
		 *
		 * Meter account can be viewed through:
		 * (1) "Default Contact Info" through the location setting dialog.
		 * (2) "ContactInfo" tab under "My Location Settings".
		 */
		if (enterpriseLocation || isPathLocationContactInfo) {
			return ContactInfoViewMode.LocationAdmin;
		} else if (
			userRole === UserRole.MultiUser ||
			userRole === UserRole.CorpUser
		) {
			return ContactInfoViewMode.ReadOnly;
		} else if (
			userRole === UserRole.SingleUser ||
			userRole === UserRole.MultiAdmin ||
			userRole === UserRole.LocationAdmin
		) {
			return ContactInfoViewMode.RegularAdmin;
		} else {
			return ContactInfoViewMode.CorporationAdmin;
		}
	};

	getCustomerId = () => {
		const { customerId } = Session.getAccountInfo();
		const { enterpriseLocation } = this.props;
		const { contactInfoViewMode } = this.state;

		if (contactInfoViewMode === ContactInfoViewMode.LocationAdmin) {
			return isObject(enterpriseLocation)
				? enterpriseLocation.CustId
				: customerId;
		} else {
			return customerId;
		}
	};

	getCustomerContactInfo = async () => {
		const { validationErrors } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		const customerId = this.getCustomerId();
		const response =
			await AccountCommunication.getCustomerContactInfo(customerId);

		if (!AccountCommunication.isSuccessful(response)) {
			this.setState({
				isLoading: false,
				loadingMessage: '',
			});

			return showError({ response });
		}

		const accountContactInfo = isObject(response.data)
			? response.data.AccountContactInfo
			: {};
		const mainContactInfo = this.buildContactInfo(accountContactInfo);
		const isBusinessAddress =
			mainContactInfo.Address.IsBusinessAddress === true;

		this.setState({
			mainContactInfo,
			isBusinessAddress,

			// store the initial data
			initialMainContactInfo: { ...mainContactInfo },

			// reset validation
			validationErrors: {
				...validationErrors,
				mainContactInfoErrors: this.buildDefaultValidationObject(),
			},

			// loading spinner
			isLoading: false,
			loadingMessage: '',
		});
	};

	getCorporationContactInfo = async () => {
		const {
			modalContext: { showError },
		} = this.props;

		const response = await AccountCommunication.getCorporationContactInfo();

		if (!AccountCommunication.isSuccessful(response)) {
			this.setState({
				isLoading: false,
				loadingMessage: '',
			});

			return showError({ response });
		}

		const data = isObject(response.data) ? response.data : {};
		const mainContactInfo = this.buildContactInfo(data.MainContact);
		const billingContactInfo = this.buildContactInfo(data.BillingContact);
		const isBusinessAddress =
			mainContactInfo.Address.IsBusinessAddress === true;

		this.setState({
			mainContactInfo,
			billingContactInfo,
			isBusinessAddress,

			// store the initial data
			initialMainContactInfo: { ...mainContactInfo },
			initialBillingContactInfo: { ...billingContactInfo },

			// reset validation
			validationErrors: {
				mainContactInfoErrors: this.buildDefaultValidationObject(),
				billingContactInfoErrors: this.buildDefaultValidationObject(),
			},

			// loading spinner
			isLoading: false,
			loadingMessage: '',
		});
	};

	handleInputChange = (contactInfoName, updatedContactInfo) => {
		this.setState({
			clean: false,
			successfulContactUpdate: false,
			[contactInfoName]: { ...updatedContactInfo },
		});

		this.setContactFormStateDirty(true);
	};

	handleCheckBoxChange = e => {
		const { name, checked } = e.target;

		this.setState(
			({
				billingContactInfo,
				initialBillingContactInfo,
				isBusinessAddress,
				sameAsMainContact,
				validationErrors,
			}) => ({
				clean: false,
				successfulContactUpdate: false,
				isBusinessAddress:
					name === 'IsBusinessAddress' ? checked === true : isBusinessAddress,
				sameAsMainContact:
					name === 'SameAsMainContact' ? checked === true : sameAsMainContact,

				// reset billing contact validation errors when "Same As Main Contact" is checked
				validationErrors:
					name === 'SameAsMainContact' && checked === true
						? {
								...validationErrors,
								billingContactInfoErrors: this.buildDefaultValidationObject(),
							}
						: validationErrors,

				// restore billing contact if user unchecks "Same as Main Contact"
				billingContactInfo:
					name === 'SameAsMainContact' && checked === false
						? {
								...initialBillingContactInfo,
							}
						: billingContactInfo,
			})
		);

		if (name === 'IsBusinessAddress') {
			this.updateBusinessAddressInContactInfo(checked);
		}

		this.setContactFormStateDirty(true);
	};

	updateBusinessAddressInContactInfo = isBusinessAddress => {
		const { mainContactInfo, billingContactInfo } = this.state;

		// include Business Address checkbox value
		const mainContactInfoAddress = isObject(mainContactInfo.Address)
			? {
					...mainContactInfo.Address,
					IsBusinessAddress: isBusinessAddress,
				}
			: {};

		// include Business Address checkbox value
		const billingContactInfoAddress = isObject(billingContactInfo.Address)
			? {
					...billingContactInfo.Address,
					IsBusinessAddress: isBusinessAddress,
				}
			: {};

		this.setState({
			mainContactInfo: {
				...mainContactInfo,
				Address: mainContactInfoAddress,
			},
			billingContactInfo: {
				...billingContactInfo,
				Address: billingContactInfoAddress,
			},
		});
	};

	handleValidationOnBlur = (contactInfoName, updatedValidation = {}) => {
		// update validation object
		this.setState(({ validationErrors }) => ({
			validationErrors: {
				mainContactInfoErrors:
					contactInfoName === 'mainContactInfo'
						? { ...updatedValidation }
						: validationErrors.mainContactInfoErrors,
				billingContactInfoErrors:
					contactInfoName === 'billingContactInfo'
						? { ...updatedValidation }
						: validationErrors.billingContactInfoErrors,
			},
		}));
	};

	/**
	 * Returns an object with boolean values for each change if user updates either phone number or email
	 * @returns {Object} Object containing isEmailChanged and isPhoneNumberChanged flags
	 */
	isUserContactInfoChanged = () => {
		const { initialMainContactInfo, mainContactInfo } = this.state;

		return Util.checkContactChangesRequireVerification({
			oldPhoneNumber: initialMainContactInfo.PhoneNumber,
			newPhoneNumber: mainContactInfo.PhoneNumber,
			oldEmailAddress: initialMainContactInfo.Email,
			newEmailAddress: mainContactInfo.Email,
		});
	};

	/**
	 * Validate contact info. Return `true` if the contact info is valid.
	 * @param {string} contactInfoKey 'mainContactInfoErrors' or 'billingContactInfoErrors'.
	 * @param {object} contactInfo The contact form data.
	 * @return {boolean}
	 */
	validateForm = (contactInfoKey, contactInfo) => {
		// start with `true` state
		const validated = true;

		// validate contact info
		const validationResult = {
			FirstName: validate('FirstName', contactInfo.FirstName),
			LastName: validate('LastName', contactInfo.LastName),
			Address1: validate('Address1', contactInfo.Address.Address1),
			City: validate('City', contactInfo.Address.City),
			Zip: validate('Zip', contactInfo.Address.Zip),
			PhoneNumber: validate('PhoneNumber', contactInfo.PhoneNumber),
			Email: validate('Email', contactInfo.Email),
		};

		// show validation error(s) in the ui
		this.setState(({ validationErrors }) => ({
			validationErrors: {
				...validationErrors,
				[contactInfoKey]: validationResult,
			},
		}));

		// validated flag should be false if one field has an error message (not false)
		return reduce(
			validationResult,
			(acc, err) => {
				if (err !== false) {
					return false;
				}
				return acc;
			},
			validated
		);
	};

	// validate main contact and billing contact (if it's rendered).
	isValidated = () => {
		const {
			mainContactInfo,
			billingContactInfo,
			contactInfoViewMode,
			sameAsMainContact,
		} = this.state;

		// validate main contact form
		let isValidated = this.validateForm(
			'mainContactInfoErrors',
			mainContactInfo
		);

		// further validate billing contact form (if it's rendered)
		if (contactInfoViewMode === ContactInfoViewMode.CorporationAdmin) {
			// skip billing contact form validation if "Same As Main Contact" is checked
			if (isValidated && !sameAsMainContact) {
				isValidated = this.validateForm(
					'billingContactInfoErrors',
					billingContactInfo
				);
			}
		}

		return isValidated;
	};

	/**
	 * Return a string of changed fields to be used for the email service, e.g.,
	 * "address, email address, and phone number".
	 * @param {array} changedFields Array of updated fields.
	 * @return {string}
	 */
	infoChanged = (changedFields = []) =>
		reduce(
			changedFields,
			(result, fieldName) => {
				if (fieldName.match(/Address|FirstName|LastName|CompanyName/)) {
					// add 'address' when user updates Address and names
					return !includes(result, 'address') ? [...result, 'address'] : result;
				} else if (fieldName.match(/Email/)) {
					// add 'email address' when user updates email
					return !includes(result, 'email address')
						? [...result, 'email address']
						: result;
				} else if (fieldName.match(/PhoneNumber/)) {
					// add 'phone number' when user updates phone number
					return !includes(result, 'phone number')
						? [...result, 'phone number']
						: result;
				}

				return result;
			},
			[]
		)
			// concatenate string with ', '
			.join(', ')

			// replace last ',' with ' and'
			.replace(/,(?=[^,]*$)/, ' and');

	createEmailParams = (isCorporate = false, changedFields) => {
		const { initialMainContactInfo, contactInfoViewMode } = this.state;
		const { enterpriseLocation = {} } = this.props;

		const isLocation =
			contactInfoViewMode === ContactInfoViewMode.LocationAdmin;
		const locTitle = isLocation ? 'Corporate Location ' : '';
		const corpTitle = isCorporate ? 'Corporate ' : '';
		const subject = `Stamps.com ${locTitle || corpTitle}Contact Info Change Confirmation`;

		return {
			TemplateName: 'contactInfoChanged',
			NvPairs: {
				merchant: 'Stamps.com',
				merchantUrl: 'stamps.com',
				strEmailAddress: initialMainContactInfo.Email,
				strFirstName: initialMainContactInfo.FirstName,
				subject,
				isLocation,
				isCorporate,
				infoChanged: this.infoChanged(changedFields),
				location: enterpriseLocation.MeterAccount
					? enterpriseLocation.MeterAccount.Name
					: 'My Location',
			},
		};
	};

	closeAccountVerificationCallback = success => {
		const { contactInfoViewMode } = this.state;
		const {
			modalContext: { showError },
		} = this.props;

		if (success) {
			if (contactInfoViewMode === ContactInfoViewMode.CorporationAdmin) {
				this.updateCorporationContactInfo();
			} else {
				this.updateCustomerContactInfo();
			}
		} else {
			setTimeout(() => {
				showError({
					data: {
						title: 'Verification Required',
						description:
							'To change your phone number or email, you must verify your identity.',
						primaryButtonType: ModalButtonActionType.Ok,
						primaryButtonText: 'Close',
						handleType: 'custom',
					},
				});
			}, 0);
		}
	};

	updateCustomerContactInfo = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const accountContactInfo =
			this.buildUpdateContactInfoParamForKey('mainContactInfo');
		const isCorporate = false; // `false` for NOT updating corporate contact info

		this.setState({
			isLoading: true,
			loadingMessage: 'Updating contact info...',
		});

		const response = await AccountCommunication.updateCustomerContactInfo({
			AccountContactInfo: accountContactInfo,
			CustId: this.getCustomerId(),
		});

		if (AccountCommunication.isSuccessful(response)) {
			this.updateSuccessfulCallback(isCorporate);
		} else {
			this.setState({
				clean: false,
				isLoading: false,
				loadingMessage: '',
				successfulContactUpdate: false,
			});

			showError({ response });
		}
	};

	updateCorporationContactInfo = async () => {
		const {
			modalContext: { showError },
		} = this.props;
		const { sameAsMainContact } = this.state;
		const params = {};
		const mainContact =
			this.buildUpdateContactInfoParamForKey('mainContactInfo');
		const billingContact =
			this.buildUpdateContactInfoParamForKey('billingContactInfo');
		const isCorporate = true; // `true` for updating corporate contact info

		if (!isEmpty(mainContact)) {
			params.MainContact = mainContact;
		}

		if (sameAsMainContact && !isEmpty(mainContact)) {
			params.BillingContact = { ...mainContact };
		} else if (!isEmpty(billingContact)) {
			params.BillingContact = billingContact;
		}

		this.setState({
			isLoading: true,
			loadingMessage: 'Updating contact info...',
		});

		const response =
			await AccountCommunication.updateCorporationContactInfo(params);

		if (AccountCommunication.isSuccessful(response)) {
			this.updateSuccessfulCallback(isCorporate);
		} else {
			this.setState({
				clean: false,
				isLoading: false,
				loadingMessage: '',
				successfulContactUpdate: false,
			});

			showError({ response });
		}
	};

	updateSuccessfulCallback = isCorporate => {
		const {
			sameAsMainContact,
			mainContactInfo,
			billingContactInfo,
			initialBillingContactInfo,
		} = this.state;

		// build new initial contact objects
		const newIniitalMainContactInfo = { ...mainContactInfo };
		const newInitialBillingContactInfo = sameAsMainContact
			? { ...mainContactInfo }
			: { ...billingContactInfo };

		// build changed fields array
		const changedFields = this.buildChangedFields(isCorporate);

		// send out a confirmation email if there are changed fields
		if (changedFields.length > 0) {
			AccountCommunication.sendEmail(
				this.createEmailParams(isCorporate, changedFields)
			);
		}

		// log after user successfully saves changes
		Logger.logToServer({
			category: 'Save Changes',
			contactType: isCorporate ? 'corporate' : 'location',
			changedFields,
		});

		// update initial main contac contact and billing contact
		this.setState({
			clean: true,
			isLoading: false,
			loadingMessage: '',
			successfulContactUpdate: true,
			initialMainContactInfo: newIniitalMainContactInfo,
			initialBillingContactInfo: isCorporate
				? newInitialBillingContactInfo
				: initialBillingContactInfo,
		});

		this.setContactFormStateDirty(false);
	};

	onKeyPressEnter = e => {
		const { key } = e;

		if (key === 'Enter') {
			e.preventDefault();

			this.onSaveButtonClick();
		}
	};

	onSaveButtonClick = () => {
		const {
			modalContext: { showError, showModal },
		} = this.props;
		const { accountStatus } = Session.getAccountInfo() || {};
		const { isEmailChanged, isPhoneNumberChanged } =
			this.isUserContactInfoChanged();
		const { contactInfoViewMode } = this.state;

		Logger.saveActionData('Save Contact Info Button Clicked');

		if (accountStatus === AccountStatus.AdminHold) {
			showError({
				data: {
					title: 'Account Status Error',
					description: `<p>Due to the current status of your account, you cannot complete this action.</p><p>Please call Customer Support at ${Util.getFraudNumber()} between 8 am – 8 pm Central Time.</p>`,
					primaryButtonType: ModalButtonActionType.Ok,
					primaryButtonText: 'OK',
					handleType: 'custom',
				},
			});
		}

		if (this.isValidated()) {
			if (isPhoneNumberChanged || isEmailChanged) {
				showModal({
					component: AccountVerificationModal,
					data: {
						completionCallback: this.closeAccountVerificationCallback,
						isChangingEmailAddress: isEmailChanged,
					},
				});

				return;
			}

			if (contactInfoViewMode === ContactInfoViewMode.CorporationAdmin) {
				this.updateCorporationContactInfo();
			} else {
				this.updateCustomerContactInfo();
			}
		}
	};

	// conditionally render "Business Address" checkbox
	renderBusinessAddressCheckbox = () => {
		const {
			layoutContext: { isPhoneLayout },
		} = this.props;
		const { contactInfoViewMode, isBusinessAddress } = this.state;

		// Per Andi, the checkbox value is not read or saved for corp account
		if (contactInfoViewMode !== ContactInfoViewMode.CorporationAdmin) {
			return (
				<div className="business-address-info">
					<WrappedCheckbox
						className={`business-address-checkbox${
							isPhoneLayout === true ? ' mobile-wrapper' : ''
						}`}
						name="IsBusinessAddress"
						disabled={contactInfoViewMode === ContactInfoViewMode.ReadOnly}
						checked={isBusinessAddress}
						onChange={this.handleCheckBoxChange}
						inputId="isBusinessAddressCheckBox"
						htmlFor="isBusinessAddressCheckBox"
						dataqa="isBusinessAddressCheckBox"
					>
						This is a business address
					</WrappedCheckbox>
					<i
						className="pi pi-info-circle address-icon-tooltip business-address-icon"
						role="img"
						tabIndex={0}
						aria-label="Business Address Info"
					/>
					<WrappedTooltip
						target="address-icon-tooltip"
						position="top"
						tooltip="For business addresses, check this box to ensure accurate rates, delivery estimates, and pickup eligibility."
					/>
				</div>
			);
		}

		return null;
	};

	render() {
		const {
			mainContactInfo,
			billingContactInfo,
			validationErrors: { mainContactInfoErrors, billingContactInfoErrors },
			contactInfoViewMode,
			isLoading,
			loadingMessage,
			clean,
			successfulContactUpdate,
			sameAsMainContact,
		} = this.state;
		const {
			layoutContext: { isMobile, isPhoneLayout },
			enterpriseLocation,
		} = this.props;
		const readOnlyContact =
			contactInfoViewMode === ContactInfoViewMode.ReadOnly;
		const mobileClassName = isPhoneLayout === true ? 'mobile-wrapper' : '';
		const enterpriseLocationName = enterpriseLocation
			? enterpriseLocation.MeterAccount.Name
			: '';

		return (
			<div className="account-contact-info">
				<div className="account-title">
					{contactInfoViewMode === ContactInfoViewMode.LocationAdmin
						? `${enterpriseLocationName} Contact Info`
						: contactInfoViewMode}
					{successfulContactUpdate && (
						<SaveConfirmation success text="Changes Saved" />
					)}
				</div>
				<div className={`account-contact-info-body ${mobileClassName}`}>
					<div className={`main-contact-wrapper ${mobileClassName}`}>
						<div className={`header-and-checkbox ${mobileClassName}`}>
							<p className="header-text">
								<b>Main Contact</b>
							</p>
						</div>
						<ContactInfoForms
							contactInfoName="mainContactInfo" // indicating the data source in the state
							contactInfo={mainContactInfo}
							contactInfoErrors={mainContactInfoErrors}
							isMobile={isMobile}
							mobileClassName={mobileClassName}
							readOnlyContact={readOnlyContact}
							handleInputChange={this.handleInputChange}
							handleValidationOnBlur={this.handleValidationOnBlur}
							onKeyPressEnter={this.onKeyPressEnter}
						/>
						{this.renderBusinessAddressCheckbox()}
					</div>
					{contactInfoViewMode === ContactInfoViewMode.CorporationAdmin && (
						<div className={`business-contact-wrapper ${mobileClassName}`}>
							<div className={`header-and-checkbox ${mobileClassName}`}>
								<p className="header-text">
									<b>Billing Contact</b>
								</p>
								<WrappedCheckbox
									className={mobileClassName}
									name="SameAsMainContact"
									checked={sameAsMainContact}
									onChange={this.handleCheckBoxChange}
									inputId="sameAsMainContactCheckbox"
									htmlFor="sameAsMainContactCheckbox"
									dataqa="sameasmaincontactcheckbox"
								>
									Same As Main Contact
								</WrappedCheckbox>
							</div>
							<ContactInfoForms
								contactInfoName="billingContactInfo" // indicating the data source in the state
								contactInfo={
									sameAsMainContact ? mainContactInfo : billingContactInfo
								}
								contactInfoErrors={billingContactInfoErrors}
								isMobile={isMobile}
								mobileClassName={mobileClassName}
								readOnlyContact={readOnlyContact}
								sameAsMainContact={sameAsMainContact}
								handleInputChange={this.handleInputChange}
								handleValidationOnBlur={this.handleValidationOnBlur}
								onKeyPressEnter={this.onKeyPressEnter}
							/>
						</div>
					)}
				</div>
				{!readOnlyContact && (
					<WrappedButton
						className={`save-button ${mobileClassName}`}
						label="Save"
						disabled={readOnlyContact || clean || isLoading}
						onClick={this.onSaveButtonClick}
						dataqa="contact-info-save"
					/>
				)}
				{isLoading && <Spinner msg={loadingMessage} />}
			</div>
		);
	}
}

export default withLayoutAndModalContexts(ContactInfo);

ContactInfo.propTypes = {
	// (Optional) Data for a single location when the user is a LocationAdmin
	enterpriseLocation: PropTypes.object,
};
